// src/services/ai/transcriptionService.ts
import { supabase } from '../supabase/client.ts';

/**
 * Transcribes audio using OpenAI Whisper via Supabase Edge Functions
 * 
 * @param audioBlob The audio blob to transcribe
 * @param language The language code (e.g., 'en-US', 'es-ES')
 * @returns The transcribed text
 */
export async function transcribeAudio(audioBlob: Blob, language: string): Promise<string> {
  try {
    // Get MIME type and ensure it's valid
    let mimeType = audioBlob.type;
    console.log("Original audio MIME type:", mimeType);
    
    // If Safari gives an empty or invalid MIME type, set a default
    if (!mimeType || mimeType === '') {
      mimeType = 'audio/mp4';
      console.log("Empty MIME type detected, defaulting to:", mimeType);
    }
    
    // Create a new blob with the correct MIME type if necessary
    let processedBlob = audioBlob;
    if (audioBlob.type !== mimeType) {
      processedBlob = new Blob([await audioBlob.arrayBuffer()], { type: mimeType });
      console.log("Created new blob with MIME type:", mimeType);
    }
    
    // Check blob size but allow processing of zero-sized blobs from Safari
    // (Safari sometimes reports size 0 for blobs that actually contain data)
    if (processedBlob.size === 0) {
      console.warn("Audio blob reports size 0 - this might be a Safari reporting issue");
    }
    
    console.log("Processing audio blob: type =", processedBlob.type, ", size =", processedBlob.size, "bytes");
    
    // Convert blob to base64
    const buffer = await processedBlob.arrayBuffer();
    
    // Handle potentially large blobs safely
    let base64data = '';
    try {
      // Use FileReader for all browsers - it's more reliable for handling large blobs
      // and has better compatibility across browsers
      try {
        // Use FileReader which works reliably in all browsers
        base64data = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            // FileReader result looks like: "data:audio/mp4;base64,ACTUAL_BASE64_DATA"
            // We need to extract just the base64 part
            const result = reader.result as string;
            if (result && typeof result === 'string') {
              const parts = result.split(',');
              if (parts.length >= 2) {
                const base64 = parts[1];
                resolve(base64);
              } else {
                console.error("Invalid FileReader result format:", result);
                reject(new Error("Invalid FileReader result format"));
              }
            } else {
              reject(new Error("FileReader returned null or invalid result"));
            }
          };
          reader.onerror = (event) => {
            console.error("FileReader error:", event);
            reject(new Error("FileReader failed to read the blob"));
          };
          
          // Log blob details before reading
          console.log("Reading blob:", processedBlob.type, processedBlob.size, "bytes");
          reader.readAsDataURL(processedBlob);
        });
        console.log("Converted audio to base64 using FileReader, length:", base64data?.length || 0);
      } catch (fileReaderError) {
        console.error("FileReader conversion failed:", fileReaderError);
        
        // Fallback to manual conversion for small files only
        if (buffer.byteLength < 1024 * 1024) { // Less than 1MB
          console.log("Falling back to manual base64 conversion");
          const bytes = new Uint8Array(buffer);
          let binary = '';
          const len = bytes.byteLength;
          for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
          }
          base64data = btoa(binary);
          console.log("Successfully converted audio to base64 using fallback, length:", base64data.length);
        } else {
          throw new Error("Audio file too large for fallback conversion");
        }
      }
      
        // Check if base64 conversion was successful
      if (!base64data || base64data.length === 0) {
        throw new Error("Failed to convert audio to base64");
      }
      
      // Verify we have valid base64 data (important!)
      if (!/^[A-Za-z0-9+/=]+$/.test(base64data)) {
        console.error("Invalid base64 string detected");
        throw new Error("Invalid base64 encoding characters detected");
      }
    
      // Call the transcription edge function
      const response = await supabase.functions.invoke('transcribe-audio', {
        body: { 
          audio: base64data,
          language: language,
          mimeType: processedBlob.type, // Send the MIME type to the server
          // Optional: add parameters like sampling rate, model size, etc.
          options: {
            modelSize: 'medium', // 'tiny', 'base', 'small', 'medium', or 'large'
          }
        }
      });
      
      // Check for errors in the response
      if (response.error) {
        throw new Error(response.error.message || "Error invoking transcription function");
      }
      
      return response.data?.text || '';
      
    } catch (e) {
      console.error("Audio processing error:", e);
      throw new Error(`Failed to process audio: ${e instanceof Error ? e.message : String(e)}`);
    }
  } catch (error) {
    console.error('Transcription service error:', error);
    throw error;
  }
}

/**
 * Enhances a transcription by correcting grammar and formatting as a task
 * 
 * @param text The transcribed text
 * @param language The language code
 * @returns Enhanced and formatted text
 */
export async function enhanceTranscription(text: string, language: string): Promise<string> {
  try {
    const { data, error } = await supabase.functions.invoke('enhance-text', {
      body: { 
        text,
        language,
        type: 'todo'
      }
    });
    
    if (error) throw new Error(error.message);
    
    return data?.enhancedText || text;
  } catch (error) {
    console.error('Text enhancement error:', error);
    // Fall back to original text if enhancement fails
    return text;
  }
}